import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { catsApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';
import FeederCatCard from '../components/FeederCatCard';
import Modal from '../components/Modal';

const FeederCatsPage = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [catToDelete, setCatToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for success message from query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get('filter');
    if (filter) {
      setFilterStatus(filter);
    }
    
    // Check for success message in location state
    if (location.state?.success && location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location]);
  
  // Fetch feeder's cats
  useEffect(() => {
    const fetchCats = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const catsData = await catsApi.getFeederCats();
        setCats(catsData || []);
      } catch (error) {
        console.error('Error fetching cats:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    fetchCats();
  }, []);
  
  // Filter cats based on search term and filter status
  const filteredCats = cats.filter(cat => {
    const matchesSearch = cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cat.description && cat.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'requests') return matchesSearch && (cat.pendingRequests > 0);
    if (filterStatus === 'no-requests') return matchesSearch && (cat.pendingRequests === 0);
    
    return matchesSearch;
  });
  
  // Handle cat delete
  const openDeleteModal = (cat) => {
    setCatToDelete(cat);
    setDeleteModalOpen(true);
    setDeleteError(null);
  };
  
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setCatToDelete(null);
    setDeleteError(null);
  };
  
  const confirmDeleteCat = async () => {
    if (!catToDelete) return;
    
    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      await catsApi.deleteCat(catToDelete.id);
      
      // Remove cat from state
      setCats(prevCats => prevCats.filter(cat => cat.id !== catToDelete.id));
      
      // Show success message
      setSuccessMessage(`${catToDelete.name} has been deleted successfully.`);
      
      // Close modal
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting cat:', error);
      setDeleteError(handleApiError(error));
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Handle action buttons
  const handleFeedCat = (cat) => {
    navigate(`/feeder/create-feeding/${cat.id}`);
  };
  
  const handleEditCat = (cat) => {
    navigate(`/feeder/edit-cat/${cat.id}`);
  };
  
  const handleDetailsClick = (cat) => {
    navigate(`/feeder/cat/${cat.id}`);
  };
  
  return (
    <div className="feeder-cats-page">
      <div className="page-header">
        <h1 className="page-title">My Cats</h1>
        <p className="page-description">
          Manage and feed your cats
        </p>
      </div>
      
      {/* Success message */}
      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
          <button className="close-button" onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}
      
      {/* Actions bar */}
      <div className="actions-bar">
        <div className="search-filter-container">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search cats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <div className="filter-dropdown">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Cats</option>
              <option value="requests">With Requests</option>
              <option value="no-requests">No Requests</option>
            </select>
          </div>
        </div>
        
        <Link to="/feeder/add-cat" className="button">
          Add New Cat
        </Link>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button className="button" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )}
      
      {/* Empty state */}
      {!loading && !error && filteredCats.length === 0 && (
        <div className="empty-state">
          {searchTerm || filterStatus !== 'all' ? (
            <>
              <div className="empty-state-icon">🔍</div>
              <h2 className="empty-state-title">No cats found</h2>
              <p className="empty-state-description">
                No cats match your search criteria. Try adjusting your filters.
              </p>
              <button 
                className="button"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
              >
                Clear Filters
              </button>
            </>
          ) : (
            <>
              <div className="empty-state-icon">🐱</div>
              <h2 className="empty-state-title">No cats yet</h2>
              <p className="empty-state-description">
                You haven't added any cats yet. Add your first cat to start tracking feedings.
              </p>
              <Link to="/feeder/add-cat" className="button">
                Add a Cat
              </Link>
            </>
          )}
        </div>
      )}
      
      {/* Cats grid with enhanced functionality */}
      {!loading && !error && filteredCats.length > 0 && (
        <div className="feeder-cats-grid">
          {filteredCats.map(cat => (
            <FeederCatCard 
              key={cat.id} 
              cat={cat} 
              onEdit={handleEditCat}
              onDelete={openDeleteModal}
              onDetails={handleDetailsClick}
            />
          ))}
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteModalOpen && catToDelete && (
        <Modal 
          isOpen={deleteModalOpen}
          onClose={closeDeleteModal}
          title="Delete Cat"
        >
          <div className="delete-modal-content">
            <p>Are you sure you want to delete <strong>{catToDelete.name}</strong>?</p>
            <p className="warning-text">This action cannot be undone. All feeding records will be kept but will be disassociated from this cat.</p>
            
            {deleteError && (
              <div className="error-message">
                <p>{deleteError}</p>
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className="button secondary-button"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              
              <button 
                className="button delete-button"
                onClick={confirmDeleteCat}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Cat'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default FeederCatsPage; 